import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { Flex, Grid, Stack } from '@mantine/core';
import debounce from 'debounce-promise';
import numeral from 'numeral';
import type { JSX } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IDailyUsageTotal, IEmailUsageRecord } from '@feathr/blackbox';
import {
  AreaChart,
  AsyncDisplay,
  Button,
  CardV2 as Card,
  Heading,
  Icon,
  MonthSwitcher,
  showError,
  Skeleton,
  StatBox,
} from '@feathr/components';
import { type MonthName, MONTHS } from '@feathr/components/dist/MonthSwitcher';
import FeatureUsageLimit from '@feathr/extender/components/FeatureUsageLimit';
import { useStore } from '@feathr/extender/state';

import type { ITransformedAreaChartData } from './EmailLimits.types';
import EmailLimitsExportModal from './EmailLimitsExportModal';
import { transformAreaChartData } from './EmailLimitsPage.transformers';

function CurrentBillingPeriodCard(): JSX.Element {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [monthlyUsage, setMonthlyUsage] = useState<IEmailUsageRecord | null>(null);
  const [areaChartData, setAreaChartData] = useState<ITransformedAreaChartData[]>([]);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const { EmailUsageRecords } = useStore();

  const debouncedMonthChange = useMemo(
    () =>
      debounce((date: Date) => {
        setSelectedDate(date);
      }, 500),
    [],
  );

  useEffect(() => {
    async function loadData(): Promise<void> {
      setIsLoading(true);
      try {
        const monthlyUsageRecord = await EmailUsageRecords.getUsageByMonth({
          target_date: selectedDate.toISOString(),
        });
        const dailyTotals = monthlyUsageRecord?.daily_totals ?? ([] as IDailyUsageTotal[]);
        const transformedDailyTotals: ITransformedAreaChartData[] =
          dailyTotals.map(transformAreaChartData);
        setMonthlyUsage(monthlyUsageRecord);
        setAreaChartData(transformedDailyTotals);
      } catch (error) {
        showError(error, t);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [selectedDate]);

  const handleMonthChange = (monthName: MonthName, year: number): void => {
    // Convert month name to number and format as YYYY-MM
    const monthNumber = MONTHS.indexOf(monthName);

    setMonthlyUsage(null);
    setAreaChartData([]);

    debouncedMonthChange(new Date(year, monthNumber, 1));
  };

  const emailSendLimit = monthlyUsage?.billing.quota ?? 0;
  const remaining = monthlyUsage ? emailSendLimit - monthlyUsage.total : 0;

  // We only have data for Oct 2025 and later
  const minDate = new Date('2025-10-01').toISOString();

  return (
    <Card width={'full'}>
      <Card.Header title={'Overview'}>
        <MonthSwitcher
          allowFuture={false}
          height={'var(--button-with-icon-size)'}
          minDate={minDate}
          onChanged={handleMonthChange}
        />
      </Card.Header>
      <Card.Content>
        <Grid>
          <Grid.Col span={4}>
            <StatBox label={'Monthly Allocation'} primary={numeral(emailSendLimit).format('0,0')} />
          </Grid.Col>
          <Grid.Col span={4}>
            <StatBox
              label={'Sent'}
              primary={monthlyUsage ? numeral(monthlyUsage.total).format('0,0') : '0'}
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <StatBox
              label={'Available Remaining'}
              primary={monthlyUsage ? numeral(remaining).format('0,0') : '0'}
            />
          </Grid.Col>
        </Grid>
      </Card.Content>

      {/* Month Limit Display */}
      <Card.Content>
        <FeatureUsageLimit limit={emailSendLimit} type={'emails'} used={monthlyUsage?.total || 0} />
      </Card.Content>

      {/* Area Chart */}
      <Card.Content>
        <AsyncDisplay
          isLoading={isLoading}
          skeleton={<Skeleton paragraphs={3} title={true} width={'100%'} />}
        >
          <Stack>
            <Flex justify={'space-between'}>
              <Heading order={4} variant={'h4'}>
                {t('Daily Email Sends')}
              </Heading>
              <Button
                onClick={(): void => setIsExportModalOpen(true)}
                prefix={<Icon icon={faDownload} />}
              >
                Export
              </Button>
            </Flex>
            <AreaChart
              data={areaChartData}
              height={300}
              suppressTitle={true}
              xAxisKey={'date'}
              yAxisKey={'value'}
            />
          </Stack>
        </AsyncDisplay>
      </Card.Content>
      <EmailLimitsExportModal
        exportType={'daily'}
        onClose={(): void => setIsExportModalOpen(false)}
        opened={isExportModalOpen}
        targetDate={selectedDate}
      />
    </Card>
  );
}

export default CurrentBillingPeriodCard;
